/* ============================================================
   ProviderFleetOverviewCard — PF-4 (TASK-74482edf): provider
   cabinet fleet summary card. Renders the backend aggregation
   (GET /api/v1/providers/fleet-overview): «Принтеры: N · активны
   M · из каталога K · свои L», catalog coverage, and the
   «Материалы: …» distinct union line (spec §5.2). Pure render of
   derived data — no canonical state.
   ============================================================ */

import { useEffect, useState } from 'react';
import { getProviderFleetOverview } from '../api/fleetOverviewApi';
import { fleetOverviewView } from './materialsChipsView';
import type { ProviderFleetOverview } from '../types/fleet';

export function ProviderFleetOverviewCard({ refreshKey }: { refreshKey: number }) {
  const [overview, setOverview] = useState<ProviderFleetOverview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getProviderFleetOverview()
      .then((o) => { if (!cancelled) { setOverview(o); setError(null); } })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Ошибка'); });
    return () => { cancelled = true; };
  }, [refreshKey]);

  if (error) return null; // overview is a summary — never blocks the printers tab

  if (!overview) {
    return (
      <div className="card" style={{ padding: 'var(--space-lg)' }}>
        <div className="spinner" style={{ margin: '0 auto' }} />
      </div>
    );
  }

  const view = fleetOverviewView(overview);

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 'var(--space-md)', flexWrap: 'wrap' }}>
        <p style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-text-primary)', margin: 0 }}>
          {view.fleetLine}
        </p>
        {view.hasPrinters && (
          <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', margin: 0 }}>
            Каталог покрывает {view.coveragePercent} парка
          </p>
        )}
      </div>
      {overview.materials.length > 0 && (
        <div style={{ display: 'flex', gap: 'var(--space-xs)', flexWrap: 'wrap', alignItems: 'center' }}>
          {overview.materials.map((m) => (
            <span
              key={m.materialId}
              title={`На ${m.printerCount} принтерах`}
              style={{
                padding: '4px 10px',
                borderRadius: 'var(--radius-pill)',
                background: 'rgba(255,149,0,0.08)',
                color: 'var(--color-primary)',
                fontSize: 'var(--fs-caption)',
                fontWeight: 'var(--fw-medium)',
              }}
            >
              {m.materialName}
              {m.printerCount > 1 ? ` · ${m.printerCount}` : ''}
            </span>
          ))}
        </div>
      )}
      {view.hasPrinters && overview.unrestrictedPrinters > 0 && (
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', margin: 0 }}>
          {overview.unrestrictedPrinters} принтер(ов) без материалов — печатают любой материал
        </p>
      )}
    </div>
  );
}
